/** Public detail pages for events, places, activities. */
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarDays, MapPin, Navigation, Store, Tag, User } from 'lucide-react';
import {
	fetchPublicEvent, fetchPublicPlace, fetchPublicActivity, fetchPublicPlaces,
} from '@/services/api/public';
import { Container, fmtDateTime } from '@/pages/public/_shared';
import { CoverImage } from '@/components/media/Cover';
import { ShareButton } from '@/components/ShareButton';
import { PUBLIC_IDS } from '@/constants/testIds';

function Back({ to, label }) {
	return <Link to={to} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-300"><ArrowLeft size={15} /> {label}</Link>;
}
function Row({ icon: Icon, children }) {
	return children ? <div className="flex items-center gap-2 text-slate-300"><Icon size={16} className="text-emerald-300" /> {children}</div> : null;
}
function Hero({ url, icon }) {
	return (
		<CoverImage url={url} icon={icon} className="mb-6 h-56 w-full rounded-2xl border border-slate-800 sm:h-72" />
	);
}
function Directions({ lat, lng }) {
	if (lat == null || lng == null) return null;
	return (
		<a data-testid={PUBLIC_IDS.directionsBtn} target="_blank" rel="noreferrer"
			href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
			className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-300">
			<Navigation size={15} /> Come arrivare
		</a>
	);
}

export function PublicEventDetail() {
	const { slug } = useParams();
	const { data: e, isError } = useQuery({ queryKey: ['pub-event', slug], queryFn: () => fetchPublicEvent(slug) });
	const { data: places = [] } = useQuery({ queryKey: ['pub-places'], queryFn: fetchPublicPlaces });
	if (isError) return <Container><p className="text-slate-400">Evento non trovato.</p></Container>;
	if (!e) return <Container><p className="text-slate-500">Caricamento…</p></Container>;
	const place = places.find((p) => p.id === e.place_id);
	const coords = e.directions_coordinates?.length === 2 ? e.directions_coordinates : place?.location?.coordinates;
	return (
		<Container>
			<Back to="/eventi" label="Eventi" />
			<div data-testid={PUBLIC_IDS.eventDetail} className="max-w-3xl">
				<Hero url={e.cover_url} icon={CalendarDays} />
				{e.category && <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-300">{e.category}</span>}
				<h1 className="mt-3 text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "'Poppins', sans-serif" }}>{e.title}</h1>
				<div className="mt-4 space-y-2">
					<Row icon={CalendarDays}>{fmtDateTime(e.start_at)}{e.end_at ? ` — ${fmtDateTime(e.end_at)}` : ''}</Row>
					<Row icon={MapPin}>{e.location_name || place?.name}</Row>
					{e.organizer_activity_slug ? (<Row icon={Store}><Link to={`/attivita/${e.organizer_activity_slug}`} data-testid="event-organizer-activity-link" className="text-emerald-300 hover:underline">{e.organizer_activity_name}</Link></Row>) : (<Row icon={User}>{e.organizer}</Row>)}
				</div>
				{e.description && <p className="mt-6 leading-relaxed text-slate-300">{e.description}</p>}
				<div className="mt-6 flex flex-wrap items-center gap-3">
					{coords && <Directions lat={coords[1]} lng={coords[0]} />}
					<ShareButton
						title={e.title}
						text={e.description ? e.description.slice(0, 160) : `${e.title} · ${e.category || 'Evento'}`}
						url={`${window.location.origin}/eventi/${e.slug}`}
					/>
				</div>
			</div>
		</Container>
	);
}

export function PublicPlaceDetail() {
	const { slug } = useParams();
	const { data: p, isError } = useQuery({ queryKey: ['pub-place', slug], queryFn: () => fetchPublicPlace(slug) });
	if (isError) return <Container><p className="text-slate-400">Luogo non trovato.</p></Container>;
	if (!p) return <Container><p className="text-slate-500">Caricamento…</p></Container>;
	const coords = p.location?.coordinates;
	return (
		<Container>
			<Back to="/luoghi" label="Luoghi" />
			<div data-testid={PUBLIC_IDS.placeDetail} className="max-w-3xl">
				<Hero url={p.cover_url} icon={MapPin} />
				{p.category && <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-300">{p.category}</span>}
				<h1 className="mt-3 text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "'Poppins', sans-serif" }}>{p.name}</h1>
				<div className="mt-4 space-y-2"><Row icon={MapPin}>{p.address}</Row></div>
				{p.description && <p className="mt-6 leading-relaxed text-slate-300">{p.description}</p>}
				<div className="mt-6 flex flex-wrap items-center gap-3">
					{coords && <Directions lat={coords[1]} lng={coords[0]} />}
					<ShareButton title={p.name} text={p.description ? p.description.slice(0, 160) : p.name}
						url={`${window.location.origin}/luoghi/${p.slug}`} testId="place-share-button" />
				</div>
			</div>
		</Container>
	);
}

export function PublicActivityDetail() {
	const { slug } = useParams();
	const { data: a, isError } = useQuery({ queryKey: ['pub-activity', slug], queryFn: () => fetchPublicActivity(slug) });
	const { data: places = [] } = useQuery({ queryKey: ['pub-places'], queryFn: fetchPublicPlaces });
	if (isError) return <Container><p className="text-slate-400">Attività non trovata.</p></Container>;
	if (!a) return <Container><p className="text-slate-500">Caricamento…</p></Container>;
	const place = places.find((p) => p.id === a.place_id);
	const coords = a.location?.coordinates?.length === 2 ? a.location.coordinates : place?.location?.coordinates;
	return (
		<Container>
			<Back to="/attivita" label="Attività" />
			<div data-testid={PUBLIC_IDS.activityDetail} className="max-w-3xl">
				<Hero url={a.cover_url} icon={Store} />
				{a.category && <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-300">{a.category}</span>}
				<h1 className="mt-3 text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "'Poppins', sans-serif" }}>{a.title}</h1>
				<div className="mt-4 space-y-2"><Row icon={Tag}>{a.category}</Row><Row icon={MapPin}>{place?.name}</Row></div>
				{a.description && <p className="mt-6 leading-relaxed text-slate-300">{a.description}</p>}
				<div className="mt-6 flex flex-wrap items-center gap-3">
					{coords && <Directions lat={coords[1]} lng={coords[0]} />}
					<ShareButton title={a.title} text={a.description ? a.description.slice(0, 160) : a.title}
						url={`${window.location.origin}/attivita/${a.slug}`} testId="activity-share-button" />
				</div>
			</div>
		</Container>
	);
}
